import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import religionJson from './assets/jsons/religion.json';
import bloodgroupJson from './assets/jsons/bloodgroup.json';
import communityJson from './assets/jsons/community.json';
import casteJson from './assets/jsons/caste.json';
import occupationJson from './assets/jsons/occupation.json';
import {
  useLazyGetStudentDetailQuery,
  useLazyGetStudentRegistrationQuery,
  useLazyGetTcClasslistQuery,
  useUpdateStudentDetailsMutation,
  useStudentsTransferSaveMutation,
  type TcStudentRow,
} from './StudentTcDetails.service';

interface Option {
  label: string;
  value: string | number;
}

interface Props {
  row: TcStudentRow;
  schoolId: number;
  schoolTypeId: number;
  onBack: () => void;
  onSaved: () => void;
}

// Form state: this Angular FormGroup has ~60 controls, so a single keyed object
// with a setField helper is used rather than 60 individual useState hooks.
type FormState = Record<string, unknown>;

// Hard-coded option lists ported from getDropdownLists().
const GENDER: Option[] = [
  { label: 'Male', value: '1' },
  { label: 'Female', value: '2' },
  { label: 'Transgender', value: '3' },
];
const PROMOTE: Option[] = [
  { value: '1', label: 'Yes' },
  { value: '2', label: 'No' },
  { value: '3', label: 'No-Discontinued' },
  { value: '4', label: 'Refer Marksheet' },
];
const COMMUNITY_TC_OPTION: Option[] = [
  { value: '1', label: 'Leave Blank' },
  { value: '2', label: 'Refer Community Certificate' },
  { value: '3', label: 'No Caste / Community' },
];
const FIRST_LANGUAGE: Option[] = [
  { value: 16, label: 'Tamil' },
  { value: 19, label: 'English' },
  { value: 17, label: 'Telugu' },
  { value: 5, label: 'Kannada' },
  { value: 18, label: 'Urdu' },
  { value: 4, label: 'Hindi' },
  { value: 8, label: 'Malayalam' },
  { value: 3, label: 'Gujarati' },
  { value: 14, label: 'Sanskrit' },
  { value: 29, label: 'French' },
  { value: 30, label: 'German' },
  { value: 31, label: 'Arabic' },
];
const MEDIUM_LANGUAGE: Option[] = [
  { value: '16', label: 'Tamil' },
  { value: '19', label: 'English' },
  { value: '17', label: 'Telugu' },
  { value: '5', label: 'Kannada' },
  { value: '18', label: 'Urdu' },
  { value: '4', label: 'Hindi' },
  { value: '8', label: 'Malayalam' },
  { value: '3', label: 'Gujarati' },
  { value: '14', label: 'Sanskrit' },
  { value: '31', label: 'Arabic' },
];

// Class labels for the c1..c12 medium matrix.
const ROMAN = [
  '',
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'XI',
  'XII',
];

// Master-data JSON typings (only the fields we read).
const RELIGION = (religionJson as { religion: Option[] }).religion;
const BLOODGROUP = (bloodgroupJson as { bloodgroup: Option[] }).bloodgroup;
const ALL_COMMUNITY = (
  communityJson as {
    community: { id: number; community_name: string; religion_id: number }[];
  }
).community;
const ALL_CASTE = (
  casteJson as {
    caste: { id: string; caste_name: string; community_id: string }[];
  }
).caste;
const OCCUPATION: Option[] = (
  occupationJson as { occupation: { id: string; occu_name: string }[] }
).occupation.map((o) => ({ label: o.occu_name, value: o.id }));

// Map an unknown master-data array into { label, value } options.
const toOptions = (
  list: unknown,
  labelKey: string,
  valueKey: string
): Option[] =>
  (Array.isArray(list) ? list : []).map((item) => {
    const row = item as Record<string, unknown>;
    return {
      label: String(row[labelKey] ?? ''),
      value: row[valueKey] as string | number,
    };
  });

// yyyy-MM-dd for <input type="date">.
const toDateInput = (value: unknown): string => {
  if (!value) return '';
  const d = new Date(value as string);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const StudentEditForm: React.FC<Props> = ({
  row,
  schoolId,
  schoolTypeId,
  onBack,
  onSaved,
}) => {
  const toast = useRef<Toast>(null);
  const notify = (
    severity: 'success' | 'error' | 'warn' | 'info',
    detail: string
  ): void => toast.current?.show({ severity, detail, life: 4000 });

  const [form, setForm] = useState<FormState>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);

  // Master-data option lists (from studentRegistration + Classlist).
  const [academic, setAcademic] = useState<Option[]>([]);
  const [incomes, setIncomes] = useState<Option[]>([]);
  const [launguages, setLaunguages] = useState<Option[]>([]);
  const [disabilities, setDisabilities] = useState<Option[]>([]);
  const [schooldist, setSchooldist] = useState<Option[]>([]);
  const [classListArr, setClassListArr] = useState<Option[]>([]);
  const [allClasses, setAllClasses] = useState<
    { class_id: string | number; section: string; school_medium_id?: string }[]
  >([]);
  const [sectionListArr, setSectionListArr] = useState<Option[]>([]);

  // Cascade-derived lists.
  const [community, setCommunity] = useState<Option[]>([]);
  // Kept for parity with Angular (caste not rendered in the template).
  const [, setCasteList] = useState<Option[]>([]);

  // c-matrix control: which classes are active/required and current-class lock.
  const [cActive, setCActive] = useState<number[]>([]);
  const [showCMatrix, setShowCMatrix] = useState(false);
  const [currentClassKey, setCurrentClassKey] = useState<string>('');

  const oldStudyingId = row.class_studying_id;

  const [getDetail] = useLazyGetStudentDetailQuery();
  const [getRegistration] = useLazyGetStudentRegistrationQuery();
  const [getClasslist] = useLazyGetTcClasslistQuery();
  const [updateStudentDetails] = useUpdateStudentDetailsMutation();
  const [studentsTransferSave] = useStudentsTransferSaveMutation();

  const setField = (name: string, value: unknown): void =>
    setForm((f) => ({ ...f, [name]: value }));

  const buildCommunity = (religionId: unknown): Option[] =>
    ALL_COMMUNITY.filter((c) => c.religion_id === Number(religionId)).map(
      (c) => ({ label: c.community_name, value: c.id })
    );
  const buildCaste = (communityId: unknown): Option[] =>
    ALL_CASTE.filter((c) => c.community_id === String(communityId)).map(
      (c) => ({
        label: c.caste_name,
        value: c.id,
      })
    );

  const sectionsForClass = (
    classId: unknown,
    classes: typeof allClasses
  ): Option[] =>
    classes
      .filter((c) => String(c.class_id) === String(classId))
      .map((c) => ({ label: c.section, value: c.section }));

  // ── load master data + student detail, then populate the form ────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [clsRes, regRes, detailRes] = await Promise.all([
          getClasslist({ schoolId }).unwrap(),
          getRegistration({ schoolId }).unwrap(),
          getDetail({
            schoolId,
            stuId: String(row.student_id_in_child_dtl ?? ''),
          }).unwrap(),
        ]);
        if (cancelled) return;

        // Classes
        const classes = (clsRes.classlist ??
          []) as unknown as typeof allClasses;
        setAllClasses(classes);
        const seen = new Set<string>();
        const clsOpts: Option[] = [];
        (clsRes.classlist ?? []).forEach((c) => {
          const id = String(c.class_id);
          if (!seen.has(id)) {
            seen.add(id);
            clsOpts.push({
              label: String(c.class_studying),
              value: c.class_id as string | number,
            });
          }
        });
        setClassListArr(clsOpts);

        // Master data
        const master = (regRes.result ?? {}) as Record<string, unknown>;
        setIncomes(toOptions(master.incomes, 'income_value', 'id'));
        setLaunguages(toOptions(master.launguages, 'MEDINSTR_DESC', 'ID'));
        setDisabilities([
          { label: 'Not Applicable', value: '0' },
          ...toOptions(master.disabilities, 'da_name', 'id'),
        ]);
        setSchooldist([
          ...toOptions(master.schooldist, 'district_name', 'id'),
          { label: 'OTHERS', value: '38' },
        ]);
        setAcademic(toOptions(master.academic, 'academic_teacher', 'id'));

        // Student detail (studentList1) + row → form
        const detail = (detailRes.result?.[0] ?? {}) as Record<string, unknown>;
        const initial: FormState = {
          ...row,
          ...detail,
          dob: toDateInput(detail.dob),
          doj: toDateInput((detail.doj as string) || new Date().toISOString()),
          student_admission_date: toDateInput(
            (detail.doj as string) || new Date().toISOString()
          ),
          bloodgroup: detail.bloodgroup ? Number(detail.bloodgroup) : '',
          religion_id: detail.religion_id ? Number(detail.religion_id) : '',
          community_id: detail.community_id ? Number(detail.community_id) : '',
          name_tamil: detail.name_tamil ?? '',
          mother_name_tamil: detail.mother_name_tamil ?? '',
          father_name_tamil: detail.father_name_tamil ?? '',
          guardian_name_tamil: detail.guardian_name_tamil ?? '',
          image_name: detail.photo || null,
          student_first_lang:
            !detail.student_first_lang || detail.student_first_lang === '0'
              ? ''
              : Number(detail.student_first_lang),
          student_medium_inst:
            row.education_medium_id ?? detail.student_medium_inst,
          student_current_class_id: oldStudyingId,
          student_id_in_child_dtl: row.student_id_in_child_dtl,
          // c1..c12 from detail
          ...Object.fromEntries(
            Array.from({ length: 12 }, (_, i) => [
              `c${i + 1}`,
              detail[`c${i + 1}`] ?? '',
            ])
          ),
          // TC defaults (Angular onRowEditDetails)
          student_nationality: detail.student_nationality || 'INDIAN',
          student_conduct: detail.student_conduct || 'GOOD',
          student_promote_class:
            Number(detail.class_studying_id) < 8 ||
            Number(detail.class_studying_id) > 12
              ? '1'
              : detail.student_promote_class || '',
          student_community_tc: detail.student_community_tc || '2',
          student_apply_tc: toDateInput(detail.student_apply_tc),
          student_tc_date: toDateInput(detail.student_tc_date),
          student_last_date: toDateInput(detail.student_last_date),
          student_medical_date: toDateInput(detail.student_medical_date),
        };
        setForm(initial);
        setCommunity(buildCommunity(detail.religion_id));
        setCasteList(buildCaste(detail.community_id));
        setSectionListArr(sectionsForClass(detail.class_studying_id, classes));
      } catch {
        if (!cancelled) notify('error', 'Unable to load student details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row]);

  // Religion → community cascade.
  const onReligionChange = (value: unknown): void => {
    setField('religion_id', value);
    setField('community_id', '');
    setCommunity(buildCommunity(value));
  };
  const onCommunityChange = (value: unknown): void => {
    setField('community_id', value);
    setCasteList(buildCaste(value));
  };

  const onClassStudyingChange = (value: unknown): void => {
    setField('class_studying_id', value);
    setField('class_section', '');
    setSectionListArr(sectionsForClass(value, allClasses));
  };

  // foreachcalss: enable/require the c-fields from the "studying from" class up
  // to the current class; lock the current class's medium.
  const onStudyingFromChange = (value: unknown): void => {
    setField('student_class_id', value);
    const currentCls = Number(form.class_studying_id);
    if ([13, 14, 15].includes(currentCls) || !currentCls) {
      setShowCMatrix(false);
      setCActive([]);
      return;
    }
    let a = Number(value);
    if (a === 13 || a === 14 || a === 15) a = 1;
    if (a > currentCls) {
      notify('error', 'From Class should not be greater than To Class');
      setField('student_class_id', '');
      setShowCMatrix(false);
      setCActive([]);
      return;
    }
    const active: number[] = [];
    const patch: FormState = {};
    for (let i = 1; i <= 12; i++) {
      if (i >= a && i <= currentCls) active.push(i);
      else patch[`c${i}`] = '';
    }
    // Lock the current class medium to student_medium_inst.
    const key = `c${currentCls}`;
    patch[key] = form.student_medium_inst;
    setForm((f) => ({ ...f, ...patch }));
    setCActive(active);
    setShowCMatrix(true);
    setCurrentClassKey(key);
  };

  // ── validation + save ─────────────────────────────────────────────────────
  const requiredBase = [
    'name',
    'name_tamil',
    'dob',
    'gender',
    'religion_id',
    'community_id',
    'mothertounge_id',
    'house_address',
    'street_name',
    'area_village',
    'district_id',
    'pin_code',
    'doj',
    'school_admission_no',
    'student_idenitiy1',
    'student_scholarship',
    'student_conduct',
    'student_promote_class',
    'student_class_id',
    'student_last_date',
    'student_first_lang',
    'student_medium_inst',
    'student_nationality',
    'student_community_tc',
    'student_apply_tc',
    'student_tc_date',
  ];

  const err = (field: string): boolean => submitted && !form[field];

  const isValid = (): boolean => {
    const reqs = [...requiredBase];
    if (form.class_studying_id) reqs.push('class_section');
    if ([2, 3, 4].includes(schoolTypeId)) reqs.push('school_recognition_no');
    for (const key of reqs) {
      if (!form[key]) return false;
    }
    if (showCMatrix) {
      for (const i of cActive) {
        if (!form[`c${i}`]) return false;
      }
    }
    return true;
  };

  const handleSave = async (): Promise<void> => {
    setSubmitted(true);
    if (!isValid()) {
      notify('warn', 'Please Fill All Mandatory Fields.!');
      return;
    }

    const toSTD = form.class_studying_id;
    const fromSTD = oldStudyingId;
    const promote_history =
      fromSTD !== toSTD
        ? {
            school_key_id: schoolId,
            student_id: form.id,
            from_class: fromSTD,
            to_class: toSTD,
          }
        : {};

    const records: Record<string, unknown> = {
      ...form,
      dob: toDateInput(form.dob),
      doj: toDateInput(form.doj),
      student_admission_date: toDateInput(form.doj),
      status: 'Active',
      transfer_flag: 0,
      updated_at: toDateInput(new Date()),
    };
    // Angular drops group_code_id unless class 11/12.
    if (!(form.class_studying_id === '11' || form.class_studying_id === '12')) {
      delete records.group_code_id;
    }

    try {
      const res = await updateStudentDetails({
        records,
        promote_history,
        constant: 'REGULAR_STUDENT',
      }).unwrap();
      if (!res.dataStatus) {
        notify('info', res.message ?? 'Unable to update details');
        return;
      }
      // Second step: studentsTransferSave with the current class medium locked.
      const currentValue: Record<string, unknown> = {
        ...records,
        student_current_class_id: oldStudyingId,
        student_school_id: schoolId,
      };
      if (currentClassKey) {
        currentValue[currentClassKey] = form.student_medium_inst;
      }
      const res2 = await studentsTransferSave(currentValue).unwrap();
      if (res2.dataStatus) {
        notify('success', res2.message ?? 'Student details saved successfully');
        onSaved();
        onBack();
      } else {
        notify('info', res2.message ?? 'Unable to save');
      }
    } catch {
      notify('error', 'Something went wrong while saving. Please try again.');
    }
  };

  const isClassLocked = !!form.class_studying_id;
  const isMediumLocked = !!row.education_medium_id;
  const showRecognition = schoolTypeId !== 1;

  const cMatrixVisible =
    showCMatrix &&
    !!form.student_class_id &&
    ![13, 14, 15].includes(Number(form.class_studying_id));

  if (loading) {
    return (
      <div className="card p-4">
        <i className="pi pi-spin pi-spinner me-2" /> Loading student details…
      </div>
    );
  }

  return (
    <div className="card card-bg-left p-4 student-edit-form">
      <Toast ref={toast} />

      {/* Personal */}
      <h3>Student Personal Information</h3>
      <hr />
      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Name of the Student in English</label>
          <InputText
            className="form-control"
            value={(form.name as string) ?? ''}
            onChange={(e) => setField('name', e.target.value.toUpperCase())}
            placeholder="Enter the name"
          />
          {err('name') && (
            <small className="text-danger">
              Student Name in English is required
            </small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Name of the Student in Tamil</label>
          <InputText
            className="form-control tamil"
            value={(form.name_tamil as string) ?? ''}
            onChange={(e) => setField('name_tamil', e.target.value)}
            placeholder="Enter the name"
          />
          {err('name_tamil') && (
            <small className="text-danger">
              Student Name in Tamil is required
            </small>
          )}
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Aadhaar Number</label>
          <InputText
            className="form-control"
            value={(form.aadhaar_uid_number as string) ?? ''}
            disabled
            placeholder="Enter Aadhaar Number"
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Date of Birth - பிறந்த தேதி</label>
          <input
            className="form-control"
            type="date"
            value={(form.dob as string) ?? ''}
            disabled
            readOnly
          />
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-3 col-md-3 col-sm-12">
          <label className="inMandatory">Gender</label>
          <Dropdown
            className="w-100"
            options={GENDER}
            value={form.gender ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Gender"
            onChange={(e) => setField('gender', e.value)}
          />
          {err('gender') && (
            <small className="text-danger">Gender is required</small>
          )}
        </div>
        <div className="col-lg-3 col-md-3 col-sm-12">
          <label>Blood Group</label>
          <Dropdown
            className="w-100"
            options={BLOODGROUP}
            value={form.bloodgroup ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Blood Group"
            onChange={(e) => setField('bloodgroup', e.value)}
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Disability Group Name (இயலாமை குழுவின் பெயர்)</label>
          <Dropdown
            className="w-100"
            options={disabilities}
            value={form.differently_abled ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Choose Disability Group"
            onChange={(e) => setField('differently_abled', e.value)}
          />
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Religion</label>
          <Dropdown
            className="w-100"
            options={RELIGION}
            value={form.religion_id ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Religion"
            onChange={(e) => onReligionChange(e.value)}
          />
          {err('religion_id') && (
            <small className="text-danger">Religion is required</small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Community</label>
          <Dropdown
            className="w-100"
            options={community}
            value={form.community_id ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Community"
            onChange={(e) => onCommunityChange(e.value)}
          />
          {err('community_id') && (
            <small className="text-danger">Community is required</small>
          )}
        </div>
      </div>

      {/* Family */}
      <hr />
      <h3>Family Details</h3>
      <hr />
      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Father Name in English</label>
          <InputText
            className="form-control"
            value={(form.father_name as string) ?? ''}
            onChange={(e) => setField('father_name', e.target.value)}
            placeholder="Enter Father Name"
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Father Name in Tamil</label>
          <InputText
            className="form-control tamil"
            value={(form.father_name_tamil as string) ?? ''}
            onChange={(e) => setField('father_name_tamil', e.target.value)}
            placeholder="Enter Father Name in Tamil"
          />
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Father Education Level</label>
          <Dropdown
            className="w-100"
            options={academic}
            value={form.father_qualify ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Father Education Level"
            onChange={(e) => setField('father_qualify', e.value)}
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Father&apos;s Occupation</label>
          <Dropdown
            className="w-100"
            options={OCCUPATION}
            value={form.father_occupation ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Occupation"
            onChange={(e) => setField('father_occupation', e.value)}
          />
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Mother Name in English</label>
          <InputText
            className="form-control"
            value={(form.mother_name as string) ?? ''}
            onChange={(e) => setField('mother_name', e.target.value)}
            placeholder="Enter Mother Name"
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Mother Name in Tamil</label>
          <InputText
            className="form-control tamil"
            value={(form.mother_name_tamil as string) ?? ''}
            onChange={(e) => setField('mother_name_tamil', e.target.value)}
            placeholder="Enter Mother Name in Tamil"
          />
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Mother Education Level</label>
          <Dropdown
            className="w-100"
            options={academic}
            value={form.mother_qualify ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Mother Education Level"
            onChange={(e) => setField('mother_qualify', e.value)}
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Mother&apos;s Occupation</label>
          <Dropdown
            className="w-100"
            options={OCCUPATION}
            value={form.mother_occupation ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Occupation"
            onChange={(e) => setField('mother_occupation', e.value)}
          />
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Guardian Name in English</label>
          <InputText
            className="form-control"
            value={(form.guardian_name as string) ?? ''}
            onChange={(e) => setField('guardian_name', e.target.value)}
            placeholder="Enter Guardian Name"
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Guardian Name in Tamil</label>
          <InputText
            className="form-control tamil"
            value={(form.guardian_name_tamil as string) ?? ''}
            onChange={(e) => setField('guardian_name_tamil', e.target.value)}
            placeholder="Enter Guardian Name in Tamil"
          />
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Guardian Education Level</label>
          <Dropdown
            className="w-100"
            options={academic}
            value={form.guardian_qualify ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Guardian Education Level"
            onChange={(e) => setField('guardian_qualify', e.value)}
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Parents Annual Income</label>
          <Dropdown
            className="w-100"
            options={incomes}
            value={form.parent_income ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Choose Parents Annual income"
            onChange={(e) => setField('parent_income', e.value)}
          />
        </div>
      </div>

      {/* Communication */}
      <hr />
      <h3>Communication Details</h3>
      <hr />
      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Mobile Number</label>
          <InputText
            className="form-control"
            value={(form.phone_number as string) ?? ''}
            disabled
            placeholder="Enter Mobile number"
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Email ID</label>
          <InputText
            className="form-control"
            value={(form.email as string) ?? ''}
            maxLength={60}
            onChange={(e) => setField('email', e.target.value)}
            placeholder="Enter Email id"
          />
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Door no./Building Name</label>
          <InputText
            className="form-control"
            value={(form.house_address as string) ?? ''}
            onChange={(e) => setField('house_address', e.target.value)}
            placeholder="Enter Door no. / Building Name"
          />
          {err('house_address') && (
            <small className="text-danger">Building Name is required</small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Street Name / Area Name</label>
          <InputText
            className="form-control"
            value={(form.street_name as string) ?? ''}
            onChange={(e) => setField('street_name', e.target.value)}
            placeholder="Enter Street Name / Area name"
          />
          {err('street_name') && (
            <small className="text-danger">Street Name is required</small>
          )}
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">City name / Village name</label>
          <InputText
            className="form-control"
            value={(form.area_village as string) ?? ''}
            onChange={(e) => setField('area_village', e.target.value)}
            placeholder="Enter City name / Village name"
          />
          {err('area_village') && (
            <small className="text-danger">City name is required</small>
          )}
        </div>
        <div className="col-lg-4 col-md-4 col-sm-12">
          <label className="inMandatory">District</label>
          <Dropdown
            className="w-100"
            options={schooldist}
            value={form.district_id ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Choose District"
            onChange={(e) => setField('district_id', e.value)}
          />
          {err('district_id') && (
            <small className="text-danger">District is required</small>
          )}
        </div>
        <div className="col-lg-2 col-md-2 col-sm-12">
          <label className="inMandatory">Pincode</label>
          <InputText
            className="form-control"
            value={(form.pin_code as string) ?? ''}
            maxLength={6}
            keyfilter="num"
            onChange={(e) => setField('pin_code', e.target.value)}
            placeholder="Enter Pincode"
          />
          {err('pin_code') && (
            <small className="text-danger">Pincode is required</small>
          )}
        </div>
      </div>

      {/* Academic */}
      <hr />
      <h3>Academic Information</h3>
      <hr />
      <div className="row">
        <div className="col-lg-4 col-md-4 col-sm-12">
          <label className="inMandatory">Class</label>
          <Dropdown
            className="w-100"
            options={classListArr}
            value={form.class_studying_id ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Choose Class"
            disabled={isClassLocked}
            onChange={(e) => onClassStudyingChange(e.value)}
          />
        </div>
        <div className="col-lg-2 col-md-2 col-sm-12">
          <label className="inMandatory">Section</label>
          <Dropdown
            className="w-100"
            options={sectionListArr}
            value={form.class_section ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Choose Section"
            onChange={(e) => setField('class_section', e.value)}
          />
          {err('class_section') && (
            <small className="text-danger">Section is required</small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">
            Date of Joining / Admission - சேர்க்கை தேதி
          </label>
          <input
            className="form-control"
            type="date"
            value={(form.doj as string) ?? ''}
            onChange={(e) => setField('doj', e.target.value)}
          />
          {err('doj') && (
            <small className="text-danger">Date of Joining is required</small>
          )}
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Medium of Instruction</label>
          <Dropdown
            className="w-100"
            options={MEDIUM_LANGUAGE}
            value={form.student_medium_inst ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select medium of instruction"
            disabled={isMediumLocked}
            onChange={(e) => setField('student_medium_inst', e.value)}
          />
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Admission Number</label>
          <InputText
            className="form-control"
            value={(form.school_admission_no as string) ?? ''}
            onChange={(e) => setField('school_admission_no', e.target.value)}
          />
          {err('school_admission_no') && (
            <small className="text-danger">Admission Number is required</small>
          )}
        </div>
      </div>

      {/* Student Transfer Certificate Details */}
      <hr />
      <h4 className="pop-highlight-text">
        Student Transfer certificate Details
      </h4>
      <hr />
      <div className="row">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">
            Personal Mark of identification 1
          </label>
          <InputText
            className="form-control"
            value={(form.student_idenitiy1 as string) ?? ''}
            onChange={(e) => setField('student_idenitiy1', e.target.value)}
            placeholder="Enter Personal Mark of identification 1"
          />
          {err('student_idenitiy1') && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>Personal Mark of identification 2</label>
          <InputText
            className="form-control"
            value={(form.student_idenitiy2 as string) ?? ''}
            onChange={(e) => setField('student_idenitiy2', e.target.value)}
            placeholder="Enter Personal Mark of identification 2"
          />
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">
            Details of Scholarship or Educational Concession Received
            <span className="note"> (Maximum Count 200 Characters)</span>
          </label>
          <InputText
            className="form-control"
            maxLength={200}
            value={(form.student_scholarship as string) ?? ''}
            onChange={(e) => setField('student_scholarship', e.target.value)}
            placeholder="Enter Details of Scholarship or Educational Concession Received"
          />
          {err('student_scholarship') && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label>
            Date of Medical inspection during the last academic year
          </label>
          <input
            className="form-control"
            type="date"
            value={(form.student_medical_date as string) ?? ''}
            onChange={(e) => setField('student_medical_date', e.target.value)}
          />
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">
            Student&apos;s conduct and character
          </label>
          <InputText
            className="form-control"
            value={(form.student_conduct as string) ?? ''}
            onChange={(e) => setField('student_conduct', e.target.value)}
            placeholder="Enter Student's conduct and character"
          />
          {err('student_conduct') && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">
            Student is Promoted to the Next class
          </label>
          <Dropdown
            className="w-100"
            options={PROMOTE}
            value={form.student_promote_class ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Promoted Type"
            onChange={(e) => setField('student_promote_class', e.value)}
          />
          {err('student_promote_class') && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">
            Student studying in the school from
          </label>
          <Dropdown
            className="w-100"
            options={classListArr}
            value={form.student_class_id ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Class"
            onChange={(e) => onStudyingFromChange(e.value)}
          />
          {err('student_class_id') && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
      </div>

      {/* c1..c12 medium matrix */}
      {cMatrixVisible && (
        <div className="row pt-3">
          {cActive.map((i) => (
            <div className="col-lg-2 col-md-3 col-sm-6 mb-2" key={i}>
              <label style={{ display: 'block' }} className="text-center">
                {ROMAN[i]}
              </label>
              <Dropdown
                className="w-100"
                options={MEDIUM_LANGUAGE}
                value={form[`c${i}`] ?? null}
                optionLabel="label"
                optionValue="value"
                placeholder="Medium"
                disabled={`c${i}` === currentClassKey}
                onChange={(e) => setField(`c${i}`, e.value)}
              />
              {submitted && !form[`c${i}`] && (
                <small className="text-danger">Required</small>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">
            Last date on which Student attended school
          </label>
          <input
            className="form-control"
            type="date"
            value={(form.student_last_date as string) ?? ''}
            min={(form.doj as string) ?? undefined}
            onChange={(e) => setField('student_last_date', e.target.value)}
          />
          <div className="note">
            (<b>Note:</b> Class I to XII : Last day of examination)
          </div>
          {err('student_last_date') && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">First Language</label>
          <Dropdown
            className="w-100"
            options={FIRST_LANGUAGE}
            value={form.student_first_lang ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select First Language"
            onChange={(e) => setField('student_first_lang', e.value)}
          />
          {err('student_first_lang') && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 mt-3">
          <label className="inMandatory">Mother tongue</label>
          <Dropdown
            className="w-100"
            options={launguages}
            value={form.mothertounge_id ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Choose Mother Tongue"
            onChange={(e) => setField('mothertounge_id', e.value)}
          />
          {err('mothertounge_id') && (
            <small className="text-danger">Mother Tongue is required</small>
          )}
        </div>
      </div>
      <div className="row pt-3">
        <div className="col-lg-6 col-md-6 col-sm-12">
          <label className="inMandatory">Student&apos;s Nationality</label>
          <InputText
            className="form-control"
            value={(form.student_nationality as string) ?? ''}
            onChange={(e) => setField('student_nationality', e.target.value)}
            placeholder="Enter Student's Nationality"
          />
          {err('student_nationality') && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
        {showRecognition && (
          <div className="col-lg-6 col-md-6 col-sm-12">
            <label>
              School Recognition Number{' '}
              {[2, 3, 4].includes(schoolTypeId) && (
                <span className="inMandatory">*</span>
              )}
            </label>
            <InputText
              className="form-control"
              value={(form.school_recognition_no as string) ?? ''}
              readOnly={![2, 3, 4].includes(schoolTypeId)}
              onChange={(e) =>
                setField('school_recognition_no', e.target.value)
              }
              placeholder="Enter School Recognition Number"
            />
            {[2, 3, 4].includes(schoolTypeId) &&
              err('school_recognition_no') && (
                <small className="text-danger">
                  Please fill out this field.
                </small>
              )}
          </div>
        )}
        <div className="col-lg-6 col-md-6 col-sm-12 pt-3">
          <label className="inMandatory">Caste / Community / Religion</label>
          <Dropdown
            className="w-100"
            options={COMMUNITY_TC_OPTION}
            value={form.student_community_tc ?? null}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Caste / Community / Religion"
            onChange={(e) => setField('student_community_tc', e.value)}
          />
          {err('student_community_tc') && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 pt-3">
          <label className="inMandatory">TC Application Date</label>
          <input
            className="form-control"
            type="date"
            value={(form.student_apply_tc as string) ?? ''}
            onChange={(e) => {
              setField('student_apply_tc', e.target.value);
              setField('student_tc_date', '');
            }}
          />
          {err('student_apply_tc') && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
        <div className="col-lg-6 col-md-6 col-sm-12 pt-3">
          <label className="inMandatory">TC Issue Date</label>
          <input
            className="form-control"
            type="date"
            min={(form.student_apply_tc as string) ?? undefined}
            value={(form.student_tc_date as string) ?? ''}
            onChange={(e) => setField('student_tc_date', e.target.value)}
          />
          {err('student_tc_date') && (
            <small className="text-danger">Please fill out this field.</small>
          )}
        </div>
      </div>

      <div className="text-end mt-4">
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-button-secondary me-2"
          onClick={onBack}
        />
        <Button
          label="Save Details"
          icon="pi pi-check"
          className="p-button-success"
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

export default StudentEditForm;
